import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Task } from './types';
import { Link } from '@/components/link/Link';
import { DownloadIcon } from '@radix-ui/react-icons';
import { Row } from '@tanstack/react-table';
import { TaskType } from '../types';
import { DropdownMenuPortal } from '@radix-ui/react-dropdown-menu';

type DownloadResultsProps = {
	row: Row<Task>;
};

type FilePathKeys = keyof Pick<
	Task,
	| 'fileReferenceData'
	| 'fileHistoricalData'
	| 'fileInitialCalibration'
	| 'fileUpdatedCalibration'
	| 'fileRevisionData'
>;

type DownloadLink = {
	[K in FilePathKeys]: {
		label: string;
		excludedFor: Array<Task['type']>;
		link: string;
	};
};

const downloadLinks: (row: Row<Task>) => DownloadLink = row => ({
	fileReferenceData: {
		label: 'Reference data',
		excludedFor: [TaskType.REVISION_DATA],
		link: row.original.fileReferenceData
	},
	fileHistoricalData: {
		label: 'Historical data',
		excludedFor: [TaskType.REVISION_DATA],
		link: row.original.fileHistoricalData
	},
	fileInitialCalibration: {
		label: 'Initial calibration data',
		excludedFor: [TaskType.REVISION_DATA],
		link: row.original.fileInitialCalibration
	},
	fileUpdatedCalibration: {
		label: 'Updated calibration data',
		excludedFor: [TaskType.GET_DATA, TaskType.REVISION_DATA],
		link: row.original.fileUpdatedCalibration
	},
	fileRevisionData: {
		label: 'Revision Data',
		excludedFor: [
			TaskType.CROSS_VALIDATION,
			TaskType.GET_DATA,
			TaskType.UNIT_VECTOR
		],
		link: row.original.fileRevisionData
	}
});

const DownloadResults = ({ row }: DownloadResultsProps) => {
	return (
		<div className="w-full flex justify-start">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="default" title="Download results">
						<DownloadIcon className="h-[1.2rem] w-[1.2rem]" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuPortal>
					<DropdownMenuContent align="start">
						{Object.entries(downloadLinks(row)).map(([key, { label, link, excludedFor }]) => {
							if (excludedFor.includes(row.original.type)) {
								return null;
							}
							return (
								<DropdownMenuItem
									key={key}
									disabled={!row.original[key as FilePathKeys] || !!row.original.filesDeletedAt}
								>
									<Link href={link} download>
										{label}
									</Link>
								</DropdownMenuItem>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenuPortal>
			</DropdownMenu>
		</div>
	);
};

export default DownloadResults;
